import {
  LOAD_ALERT_MESSAGES,
  REFRESH_ALERT_MESSAGES,
  PROCESS_ALERT_MESSAGES,
  MESSAGE_ERROR,
  loadProcessedAlertMessages,
  LOAD_MESSAGE_DETAILS,
  PROCESS_MESSAGE_DETAILS,
  updateMessage,
  loadProcessedMessagesDetails,
  CANCEL_MESSAGE,
  PROCESS_CANCELLING_MESSAGE,
} from '../actions/messagesActions';
import { apiRequest } from '../actions/apiActions';
import { showLoading, hideLoading } from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';

/**
 * Middleware to trigger an api call to retrieve all of the alert messages
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the alert messages
 */
export const loadAlertMessages = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ALERT_MESSAGES) {
    const body = null;
    let dateQuery;
    // This creates a default query just in case this middleware is called without the payload
    let today = new Date();
    today.setMonth(today.getUTCMonth() - 2);
    let currentMonth = today.getUTCMonth();
    // Javascript dates don't add a leading 0 to single digit months but the API requires all
    // months to be two digits so this checks for that and adds the 0 in front of the digit
    if (currentMonth < 10) {
      currentMonth = '0' + currentMonth;
    }
    const currentDay = today.getUTCDate();

    // Generates the query string for the api call
    // This is used to ony retrieve the data that's for the last 3 months
    let defaultQuery =
      'start_time_gte=' +
      today.getUTCFullYear() +
      '-' +
      currentMonth +
      '-' +
      currentDay;
    // Checks to see if the action has a payload, if it does use that date for the query
    if (action.payload) {
      let greaterThanDate = action.payload.greaterThanDate;
      let lessThanDate = action.payload.lessThanDate;
      let greaterThanQuery;
      if (greaterThanDate) {
        let month = greaterThanDate.getUTCMonth() + 1;
        // The months don't have a leading 0 if they're under 10 so this adds the 0
        if (month < 10) {
          month = '0' + month;
        }
        greaterThanQuery =
          'start_time_gte=' +
          greaterThanDate.getUTCFullYear() +
          '-' +
          month +
          '-' +
          greaterThanDate.getUTCDate();
      }

      let lessThanQuery;
      if (lessThanDate) {
        let month = lessThanDate.getUTCMonth() + 1;
        if (month < 10) {
          month = '0' + month;
        }
        const day = lessThanDate.getUTCDate() + 1;
        lessThanQuery =
          'start_time_lte=' +
          lessThanDate.getUTCFullYear() +
          '-' +
          month +
          '-' +
          day;
      }
      // Checks for the greater than date
      if (greaterThanQuery) {
        // checks if there's also a less than
        if (lessThanQuery) {
          // if so, creates the query string from both
          dateQuery = greaterThanQuery + '&' + lessThanQuery;
        } else {
          // if not, only creates the query from the greater than
          dateQuery = greaterThanQuery;
        }
        // if there's no greater than date but there is a less than
      } else if (lessThanQuery) {
        // creates the query from just the less than
        dateQuery = lessThanQuery;
      } else {
        // if nothing else, use the default query created above
        dateQuery = defaultQuery;
      }
    }

    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertmsglog +
        '?' +
        dateQuery,
      onSuccess: PROCESS_ALERT_MESSAGES,
      onError: MESSAGE_ERROR,
      auth: store.getState().auth.token,
      body: false,
      messages: action.payload,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to trigger an api call to retrieve the most recent 7 days of alert messages
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#apiRequest - Starts api request to retrieve the alert messages
 */
export const refeshAlertMessages = (store) => (next) => (action) => {
  next(action);
  if (action.type === REFRESH_ALERT_MESSAGES) {
    const body = null;
    let dateQuery;
    // This creates a default query just in case this middleware is called without the payload
    let today = new Date();
    today.setMonth(today.getUTCMonth() - 2);
    let currentMonth = today.getUTCMonth();
    if (currentMonth < 10) {
      currentMonth = '0' + currentMonth;
    }
    const currentDay = today.getUTCDate();

    // Generates the query string for the api call
    let defaultQuery =
      'start_time_gte=' +
      today.getUTCFullYear() +
      '-' +
      currentMonth +
      '-' +
      currentDay;
    // Checks to see if the action has a payload, if it does use that date for the query
    if (action.payload) {
      let greaterThanDate = action.payload;
      let greaterThanQuery;
      let month = greaterThanDate.getUTCMonth() + 1;
      // The months don't have a leading 0 if they're under 10 so this adds the 0
      if (month < 10) {
        month = '0' + month;
      }
      greaterThanQuery =
        'start_time_gte=' +
        greaterThanDate.getUTCFullYear() +
        '-' +
        month +
        '-' +
        greaterThanDate.getUTCDate();

      // Checks for the greater than date
      if (greaterThanQuery) {
        // If it's there, set the date query to that
        dateQuery = greaterThanQuery;
      }
    } else {
      // if nothing else, use the default query created above
      dateQuery = defaultQuery;
    }

    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertmsglog +
        '?' +
        dateQuery,
      onSuccess: PROCESS_ALERT_MESSAGES,
      onError: MESSAGE_ERROR,
      auth: store.getState().auth.token,
      body: false,
      messages: action.payload,
    };
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the returned messages array from the api request.
 * @description - This takes the array, sets the values for the message status based on if there is a completed time or cancelled time, sets the urgentMsg to a boolean value instead of just 0 or 1, then adds them to the object to be inserted into the redux store
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#loadProcessedAlertMessages - Dispatches the load processed messages action to load the returned messages into the Redux store
 */
export const processAlertMessages = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_ALERT_MESSAGES) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_ALERT_MESSAGES,
        onError: MESSAGE_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
    }
    const rawMessages = action.payload.results;
    let processedMessages = { ...store.getState().messages.alertMessages };

    rawMessages.map((message) => {
      let urgentMsg = false;
      if (message.urgent_type === 1) {
        urgentMsg = true;
      }
      let messageStatus = 'Active';
      const completeTime = new Date(message.legacy_end_time);
      const cancelledMsgTime = new Date(message.cancelled_time);
      const clearedTime = new Date(message.legacy_cleared_time);
      if (completeTime > cancelledMsgTime) {
        if (completeTime > clearedTime) {
          messageStatus = 'Completed';
        } else {
          messageStatus = 'Cleared';
        }
      } else if (cancelledMsgTime > completeTime) {
        if (cancelledMsgTime > clearedTime) {
          messageStatus = 'Cancelled';
        } else {
          messageStatus = 'Cleared';
        }
      }
      processedMessages[message.msg_id] = {
        type: 'alertMessages',
        displayType: 'Alert Message',
        msgId: message.msg_id,
        id: message.id,
        reminderNum: message.reminder_num,
        alertMsgId: message.msg_id,
        msgSender: message.msg_sender,
        title: message.msg_title,
        message: message.msg_body,
        recipientCount: message.recipient_count,
        msgType: message.msg_type,
        urgent: urgentMsg,
        startTime: message.legacy_start_time,
        completeTime: message.legacy_end_time,
        cancelledMsgTime: message.cancelled_time,
        clearedTime: message.legacy_cleared_time,
        messageStatus: messageStatus,
      };
      return '';
    });

    store.dispatch(loadProcessedAlertMessages(processedMessages));
  }
};
/**
 * Middleware to trigger an api call to retrieve the alert details for a specific alert message
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#apiRequest - Starts api request to retrieve the message response details
 */
export const loadMessageDetails = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_MESSAGE_DETAILS) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertdetails +
        '?msg_log_id=' +
        action.payload,
      onSuccess: PROCESS_MESSAGE_DETAILS,
      onError: MESSAGE_ERROR,
      auth: store.getState().auth.token,
      body: false,
      messages: action.payload,
    };
    // dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};
/**
 * Middleware to trigger an api call to retrieve the alert details for a specific alert message
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#apiRequest - Starts api request to retrieve the message response details
 */
export const cancelMessage = (store) => (next) => (action) => {
  next(action);
  if (action.type === CANCEL_MESSAGE) {
    const body = null;
    const meta = {
      method: 'PUT',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertmsglog +
        '/' +
        action.payload +
        '/cancel/',
      onSuccess: PROCESS_CANCELLING_MESSAGE,
      onError: MESSAGE_ERROR,
      auth: store.getState().auth.token,
      body: false,
      messages: action.payload,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the returned results array from the api request.
 * @description - This takes the array and sets the status to "Undelivered", "Delivered", and "Acknowledged" based on the returned information and puts all of the information in the array
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#loadProcessedMessageDetails - Dispatches the load processed units action to load the returned results into the Redux store
 */
export const processMessageDetails = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_MESSAGE_DETAILS) {
    // dispatch(hideLoading());
    let processedMessages = {
      id: action.meta.messages,
      messages: [],
    };
    const rawMessages = action.payload.results;
    rawMessages.map((message) => {
      let messageStatus = 'Undelivered';
      if (message.msg_status === 2) {
        messageStatus = 'Delivered';
      } else if (message.msg_status === 3) {
        messageStatus = 'Acknowledged';
      }
      const detail = {
        id: message.id,
        device: message.device_description,
        undelivered_time: message.undelivered_time,
        unacknowledged_time: message.unacknowledged_time,
        acknowledged_time: message.acknowledged_time,
        msg_status: messageStatus,
      };
      processedMessages.messages.push(detail);
      return '';
    });
    dispatch(hideLoading());
    dispatch(loadProcessedMessagesDetails(processedMessages));
  }
};
/**
 * Middleware to process the returned results array from the api request.
 * @description - This takes the array and sets the status to "Undelivered", "Delivered", and "Acknowledged" based on the returned information and puts all of the information in the array
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#loadProcessedMessageDetails - Dispatches the load processed units action to load the returned results into the Redux store
 */
export const processCancellingMessage = ({ dispatch }) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_CANCELLING_MESSAGE) {
    dispatch(hideLoading());

    const message = action.payload;

    let urgentMsg = false;
    if (message.urgent_type === 1) {
      urgentMsg = true;
    }
    let messageStatus = 'Active';
    const completeTime = new Date(message.end_time);
    const cancelledMsgTime = new Date(message.cancelled_time);
    const clearedTime = new Date(message.cleared_time);
    if (completeTime > cancelledMsgTime) {
      if (completeTime > clearedTime) {
        messageStatus = 'Completed';
      } else {
        messageStatus = 'Cleared';
      }
    } else if (cancelledMsgTime > completeTime) {
      if (cancelledMsgTime > clearedTime) {
        messageStatus = 'Cancelled';
      } else {
        messageStatus = 'Cleared';
      }
    }
    const processedMessage = {
      type: 'alertMessages',
      displayType: 'Alert Message',
      msgId: message.msg_id,
      id: message.id,
      reminderNum: message.reminder_num,
      alertMsgId: message.msg_id,
      msgSender: message.msg_sender,
      title: message.msg_title,
      message: message.msg_body,
      recipientCount: message.recipient_count,
      msgType: message.msg_type,
      urgent: urgentMsg,
      startTime: message.start_time,
      completeTime: message.end_time,
      cancelledMsgTime: message.cancelled_time,
      clearedTime: message.cleared_time,
      messageStatus: messageStatus,
    };

    dispatch(updateMessage(processedMessage));
  }
};

export const messagesMdl = [
  refeshAlertMessages,
  loadAlertMessages,
  processAlertMessages,
  loadMessageDetails,
  processMessageDetails,
  cancelMessage,
  processCancellingMessage,
];
